import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { MechanicOption, MaintenanceSchedulingService } from './maintenance-scheduling.service';

@Component({
  selector: 'app-select-mechanic',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './select-mechanic.view.html',
  styleUrls: ['./select-mechanic.view.css']
})
export class SelectMechanicComponent implements OnInit, OnDestroy {
  private static readonly hollowStar = String.fromCharCode(9734);
  private readonly mechanicVisualsByName = new Map<string, { costTier: '$' | '$$' | '$$$'; rating: number }>([
    ['peter griffin', { costTier: '$', rating: 2 }],
    ['stuart little', { costTier: '$$$', rating: 4 }],
    ['michael scott', { costTier: '$', rating: 1 }],
    ['percy jackson', { costTier: '$$', rating: 3 }],
    ['choji akimichi', { costTier: '$', rating: 1 }],
    ['bart simpson', { costTier: '$$', rating: 2 }],
    ['tarzan', { costTier: '$$$', rating: 3 }],
    ['tom', { costTier: '$$$', rating: 2 }],
    ['jerry', { costTier: '$$', rating: 1 }],
    ['homer', { costTier: '$$', rating: 1 }]
  ]);
  ownerId = '';
  vehicleId: number | undefined;
  partName = '';
  selectedMechanic = '';
  mechanics: MechanicOption[] = [];
  loading = false;
  errorMessage = '';

  private routeSub: Subscription | null = null;
  private mechanicsSub: Subscription | null = null;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly schedulingService: MaintenanceSchedulingService
  ) {}

  ngOnInit(): void {
    this.routeSub = this.route.queryParamMap.subscribe((params) => {
      this.ownerId = (params.get('ownerId') ?? '').trim();
      const rawVehicleId = params.get('vehicleId');
      const parsedVehicleId = rawVehicleId ? Number.parseInt(rawVehicleId, 10) : Number.NaN;
      this.vehicleId = Number.isFinite(parsedVehicleId) ? parsedVehicleId : undefined;
      this.partName = (params.get('part') ?? '').trim();

      const preferredMechanic = (params.get('mechanic') ?? '').trim();
      this.loadMechanics(preferredMechanic);
    });
  }

  ngOnDestroy(): void {
    this.routeSub?.unsubscribe();
    this.mechanicsSub?.unsubscribe();
  }

  goBack(): void {
    this.router.navigate(['/schedule-maintenance'], {
      queryParams: {
        part: this.partName,
        mechanic: this.selectedMechanic,
        ...this.sharedQueryParams()
      }
    });
  }

  setSelectedMechanic(mechanicName: string): void {
    this.selectedMechanic = mechanicName;
  }

  mechanicCostLabel(mechanicName: string): '$' | '$$' | '$$$' {
    return this.resolveVisuals(mechanicName).costTier;
  }

  mechanicRatingLabel(mechanicName: string): string {
    const rating = this.resolveVisuals(mechanicName).rating;
    return SelectMechanicComponent.hollowStar.repeat(rating);
  }

  confirmSelection(): void {
    if (!this.selectedMechanic.trim()) {
      return;
    }

    this.router.navigate(['/schedule-maintenance'], {
      queryParams: {
        part: this.partName,
        mechanic: this.selectedMechanic,
        ...this.sharedQueryParams()
      }
    });
  }

  private loadMechanics(preferredMechanic: string): void {
    this.mechanicsSub?.unsubscribe();
    this.loading = true;
    this.errorMessage = '';

    if (!this.ownerId && this.vehicleId === undefined) {
      const presetMechanics = this.schedulingService.getFallbackMechanicsSnapshot();
      this.mechanics = presetMechanics;
      const preferredExists = presetMechanics.some((item) => item.name === preferredMechanic);
      this.selectedMechanic = preferredExists ? preferredMechanic : presetMechanics[0]?.name ?? '';
      this.loading = false;
      return;
    }

    this.mechanicsSub = this.schedulingService.getMechanics().subscribe({
      next: (mechanics) => {
        this.mechanics = mechanics;
        const preferredExists = mechanics.some((item) => item.name === preferredMechanic);
        this.selectedMechanic = preferredExists ? preferredMechanic : mechanics[0]?.name ?? '';
        this.loading = false;
      },
      error: () => {
        const fallbackMechanics = this.schedulingService.getFallbackMechanicsSnapshot();
        this.mechanics = fallbackMechanics;
        const preferredExists = fallbackMechanics.some((item) => item.name === preferredMechanic);
        this.selectedMechanic = preferredExists ? preferredMechanic : fallbackMechanics[0]?.name ?? '';
        this.errorMessage = fallbackMechanics.length > 0 ? 'Showing preset mechanic defaults.' : 'Unable to load registered mechanics.';
        this.loading = false;
      }
    });
  }

  private sharedQueryParams(): Record<string, string | number> {
    const queryParams: Record<string, string | number> = {};
    if (this.ownerId) {
      queryParams['ownerId'] = this.ownerId;
    }
    if (this.vehicleId !== undefined) {
      queryParams['vehicleId'] = this.vehicleId;
    }
    return queryParams;
  }

  private resolveVisuals(mechanicName: string): { costTier: '$' | '$$' | '$$$'; rating: number } {
    const normalizedName = mechanicName.trim().toLowerCase();
    const known = this.mechanicVisualsByName.get(normalizedName);
    if (known) {
      return known;
    }

    const hash = this.stableHash(normalizedName);
    const costOptions: Array<'$' | '$$' | '$$$'> = ['$', '$$', '$$$'];
    const costTier = costOptions[hash % costOptions.length];
    const rating = (hash % 5) + 1;
    return { costTier, rating };
  }

  private stableHash(value: string): number {
    let hash = 0;
    for (let index = 0; index < value.length; index++) {
      hash = ((hash << 5) - hash + value.charCodeAt(index)) | 0;
    }

    return Math.abs(hash);
  }
}
